import { useState, useEffect, useCallback, useRef } from 'react';
import { getAudits, getSitemap, getPageSpeedKey, analyseUrl } from '../api';
import { buildAuditRows, normUrl } from '../utils/auditRows';
import { normalizePageUrl } from '../utils/referencePages';
import { useToast } from '../ToastContext';

function deduplicateAudits(auditsList, projectUrl) {
    const urlMap = new Map();
    let mainUrl = (projectUrl || '').trim();
    if (mainUrl && !mainUrl.startsWith('http')) mainUrl = `https://${mainUrl}`;
    const cleanMainUrl = normUrl(mainUrl);

    auditsList.forEach(audit => {
        const normalized = normUrl(audit.url);
        const key = normalized === cleanMainUrl ? cleanMainUrl : normalized;
        if (!urlMap.has(key)) {
            urlMap.set(key, normalized === cleanMainUrl ? { ...audit, url: cleanMainUrl } : audit);
        }
    });
    return Array.from(urlMap.values());
}

export function usePerformanceAudits(
    projectSlug,
    projectUrl,
    referencePages = [],
    dateDernierAudit = null,
) {
    const [audits, setAudits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sort, setSort] = useState({ key: 'score', dir: 'desc' });
    const [isAuditing, setIsAuditing] = useState(false);
    const [currentUrl, setCurrentUrl] = useState('');
    const [currentUrlsBatch, setCurrentUrlsBatch] = useState([]);
    const [pendingUrls, setPendingUrls] = useState([]);
    const [progress, setProgress] = useState(0);
    const [rowLoading, setRowLoading] = useState({});
    const [pagespeedKeyConfigured, setPagespeedKeyConfigured] = useState(false);
    const [selectedUrls, setSelectedUrls] = useState(new Set());

    const { showToast } = useToast();
    const mountedRef = useRef(true);
    const cancelledRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        async function checkPageSpeedKey() {
            try {
                const data = await getPageSpeedKey();
                if (mountedRef.current) setPagespeedKeyConfigured(!!data.is_configured);
            } catch (e) {
                if (mountedRef.current) setPagespeedKeyConfigured(false);
            }
        }
        checkPageSpeedKey();
    }, []);

    const referencePagesKey = (referencePages || [])
        .map((u) => normalizePageUrl(u))
        .filter(Boolean)
        .sort()
        .join('|');

    const fetchAudits = useCallback(async () => {
        if (!projectSlug) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');

        try {
            let fetchedAudits = [];
            try {
                fetchedAudits = await getAudits(projectSlug);
            } catch (auditError) {
                if (auditError.response?.status !== 404) throw auditError;
            }

            let sitemap = { urls: [] };
            try {
                sitemap = await getSitemap(projectSlug);
            } catch (sitemapError) {
                if (fetchedAudits.length > 0) {
                    sitemap = { urls: fetchedAudits.map(a => a.url).filter(Boolean) };
                }
            }

            if (!mountedRef.current) return;

            // Source unique de fusion sitemap + audits (partagée avec l'onglet SEO).
            const { rows: combinedAudits } = buildAuditRows(
                sitemap.urls || [],
                fetchedAudits,
                projectUrl,
                {
                    missingLoading: false,
                    prependHome: true,
                    extraUrls: referencePages,
                    orphanAudits: 'reference-only',
                },
            );

            setAudits(combinedAudits);
        } catch (e) {
            if (mountedRef.current) {
                const errorMessage = e.response?.data?.detail || e.message || 'Erreur lors du chargement des audits';
                setError(errorMessage);
            }
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [projectSlug, projectUrl, referencePagesKey]);

    useEffect(() => {
        if (!isAuditing) fetchAudits();
    }, [fetchAudits, isAuditing, referencePagesKey, dateDernierAudit]);

    async function fetchPendingUrls() {
        try {
            const sitemap = await getSitemap(projectSlug);
            const urls = (sitemap.urls || []).filter(u => typeof u === 'string' && u.trim().length > 0 && (u.startsWith('http://') || u.startsWith('https://')));
            if (projectUrl) {
                const homeUrl = projectUrl.startsWith('http') ? projectUrl : `https://${projectUrl}`;
                const cleanHome = normUrl(homeUrl);
                const hasHome = urls.some(u => normUrl(u) === cleanHome);
                if (!hasHome) urls.unshift(homeUrl);
            }
            return urls;
        } catch (e) {
            return [];
        }
    }

    const getSortValue = (audit, key) => {
        if (key === 'url') {
            const u = audit.url || '';
            const normalized = normUrl(u);
            let mainUrl = (projectUrl || '').trim();
            if (mainUrl && !mainUrl.startsWith('http')) mainUrl = `https://${mainUrl}`;
            if (normalized === normUrl(mainUrl)) return '';
            return normalized;
        }
        const score = audit[key];
        if (score === null || score === undefined || score === 0) return -1;
        return score;
    };

    const handleSort = (key) => {
        setSort(prev => {
            if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            return { key, dir: 'desc' };
        });
    };

    const getSortedAudits = () => {
        return deduplicateAudits([...audits], projectUrl).sort((a, b) => {
            const aHasScore = a.pagespeed_average > 0;
            const bHasScore = b.pagespeed_average > 0;
            if (aHasScore && !bHasScore) return -1;
            if (!aHasScore && bHasScore) return 1;

            const vA = getSortValue(a, sort.key);
            const vB = getSortValue(b, sort.key);
            if (vA === vB) return 0;
            if (sort.dir === 'asc') return vA > vB ? 1 : -1;
            return vA < vB ? 1 : -1;
        });
    };

    const handleAnalyseOne = async (url) => {
        if (!projectSlug || !url) return;
        setRowLoading(prev => ({ ...prev, [url]: true }));
        setAudits(prev => prev.map(row => row.url === url ? { ...row, loading: true } : row));

        try {
            const result = await analyseUrl(projectSlug, url, false);
            if (!mountedRef.current) return;

            if (result.pagespeed_error) {
                let errorMessage = result.pagespeed_error;
                let showFullAlert = false;
                if (result.pagespeed_error.includes('Clé API PageSpeed non configurée')) {
                    errorMessage = 'Clé API PageSpeed manquante. Configurez-la dans votre profil.';
                    showFullAlert = true;
                } else if (result.pagespeed_error.includes('403') || result.pagespeed_error.includes('blocked')) {
                    errorMessage = 'Permissions insuffisantes pour la clé API PageSpeed.';
                } else if (result.pagespeed_error.includes('Timeout')) {
                    errorMessage = 'Timeout PageSpeed. La page est peut-être trop lente.';
                }
                showToast(errorMessage, showFullAlert ? 'error' : 'warning');
            }

            const fullResult = { ...result, url, loading: false };
            setAudits(prev => prev.map(row => row.url === url ? fullResult : row));

            try {
                const updatedAudits = await getAudits(projectSlug);
                if (!mountedRef.current) return;
                const norm = normUrl(url);
                const latestAudit = updatedAudits.find(a => a.url === url || normUrl(a.url) === norm);
                if (latestAudit) {
                    setAudits(prev => prev.map(row => {
                        if (row.url !== url) return row;
                        return { ...latestAudit, url, loading: false, pagespeed_raw: row.pagespeed_raw || latestAudit.pagespeed_raw };
                    }));
                }
            } catch (_) { /* already updated locally */ }

            if (result.pagespeed_average > 0) {
                showToast('Analyse terminée avec succès', 'success');
            } else {
                showToast('Analyse terminée (scores PageSpeed non disponibles)', 'warning');
            }
        } catch (e) {
            if (!mountedRef.current) return;
            const msg = e.response?.data?.detail || e.message || "Erreur lors de l'analyse";
            setAudits(prev => prev.map(row =>
                row.url === url ? { ...row, loading: false, error: true, errorMsg: msg } : row
            ));
            showToast(msg, 'error');
        } finally {
            if (mountedRef.current) setRowLoading(prev => ({ ...prev, [url]: false }));
        }
    };

    const handleAudit = async (selectedUrlsList = null) => {
        if (!projectSlug) return;
        cancelledRef.current = false;
        setIsAuditing(true);
        setError('');
        setCurrentUrl('');
        setCurrentUrlsBatch([]);
        setProgress(0);

        try {
            let urls = selectedUrlsList && selectedUrlsList.length > 0
                ? selectedUrlsList
                : await fetchPendingUrls();

            if (urls.length === 0) {
                showToast('Aucune URL à analyser', 'info');
                setIsAuditing(false);
                return;
            }

            setPendingUrls(urls);
            setAudits(prev => {
                const existingAuditsMap = {};
                prev.forEach(audit => { if (audit.url) existingAuditsMap[audit.url] = audit; });

                const newAudits = urls.map(u => {
                    if (existingAuditsMap[u]) {
                        const hasExistingPageSpeed = existingAuditsMap[u].pagespeed_average > 0;
                        return { ...existingAuditsMap[u], url: u, loading: !hasExistingPageSpeed };
                    }
                    return { url: u, loading: true };
                });

                const otherAudits = prev.filter(audit => audit.url && !urls.includes(audit.url));
                return [...newAudits, ...otherAudits];
            });

            const BATCH_SIZE = 5;
            const doneCountRef = { current: 0 };

            for (let i = 0; i < urls.length; i += BATCH_SIZE) {
                if (cancelledRef.current || !mountedRef.current) break;
                const batch = urls.slice(i, i + BATCH_SIZE);
                setCurrentUrlsBatch(batch);

                const batchPromises = batch.map(async (u) => {
                    try {
                        const result = await analyseUrl(projectSlug, u, false);
                        if (mountedRef.current) {
                            setAudits(prev => prev.map(row =>
                                row.url === u ? { ...result, url: u, loading: false } : row
                            ));
                        }
                        doneCountRef.current++;
                        if (mountedRef.current) setProgress(Math.round((doneCountRef.current / urls.length) * 100));
                        return { success: true, url: u, result };
                    } catch (err) {
                        if (mountedRef.current) {
                            setAudits(prev => prev.map(row =>
                                row.url === u ? { ...row, loading: false, error: true, errorMsg: err.message } : row
                            ));
                        }
                        doneCountRef.current++;
                        if (mountedRef.current) setProgress(Math.round((doneCountRef.current / urls.length) * 100));
                        return { success: false, error: err };
                    }
                });

                await Promise.all(batchPromises);

                if (i + BATCH_SIZE < urls.length) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
            }

            if (mountedRef.current) {
                showToast(`Audit terminé : ${doneCountRef.current} pages analysées`, 'success');
                setSelectedUrls(new Set());
                try {
                    const refreshed = await getAudits(projectSlug);
                    if (mountedRef.current) {
                        setAudits(prev => {
                            const rawMap = {};
                            prev.forEach(a => { if (a.url && a.pagespeed_raw) rawMap[a.url] = a.pagespeed_raw; });
                            const auditMap = {};
                            refreshed.forEach(a => {
                                if (!a.url) return;
                                auditMap[a.url] = a;
                                const k = normUrl(a.url);
                                if (k !== a.url) auditMap[k] = a;
                            });
                            return prev.map(row => {
                                const fresh = auditMap[row.url] ?? auditMap[normUrl(row.url)];
                                if (!fresh) return row;
                                return { ...fresh, url: row.url, loading: false, pagespeed_raw: rawMap[row.url] || fresh.pagespeed_raw };
                            });
                        });
                    }
                } catch (_) { /* data already up to date */ }
            }
        } catch (e) {
            if (mountedRef.current) {
                setError(e.message || "Erreur lors de l'audit");
                showToast(e.message || "Erreur lors de l'audit", 'error');
            }
        } finally {
            if (mountedRef.current) {
                setIsAuditing(false);
                setTimeout(() => {
                    if (mountedRef.current) {
                        setCurrentUrl('');
                        setCurrentUrlsBatch([]);
                        setProgress(0);
                    }
                }, 2000);
                setPendingUrls([]);
            }
        }
    };

    const toggleUrlSelection = (url) => {
        setSelectedUrls(prev => {
            const newSet = new Set(prev);
            if (newSet.has(url)) newSet.delete(url);
            else newSet.add(url);
            return newSet;
        });
    };

    const selectAllUrls = (filteredAudits) => {
        if (selectedUrls.size === filteredAudits.length) {
            setSelectedUrls(new Set());
        } else {
            setSelectedUrls(new Set(filteredAudits.map(a => a.url)));
        }
    };

    return {
        audits,
        loading,
        error,
        sort,
        isAuditing,
        currentUrl,
        currentUrlsBatch,
        pendingUrls,
        progress,
        rowLoading,
        pagespeedKeyConfigured,
        selectedUrls,
        setSort,
        handleSort,
        handleAnalyseOne,
        handleAudit,
        getSortedAudits,
        toggleUrlSelection,
        selectAllUrls
    };
}
